/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { first, identity, isNil, isString, iteratee as getIteratee, sortedIndexBy } from 'lodash';
import { isNotNil } from './data-util';
export var identityCollectionComparator = valueComparator(identity);
export function arrayIterator(array) {
    var _i, array_1, item;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                _i = 0, array_1 = array;
                _a.label = 1;
            case 1:
                if (!(_i < array_1.length)) return [3 /*break*/, 4];
                item = array_1[_i];
                return [4 /*yield*/, item];
            case 2:
                _a.sent();
                _a.label = 3;
            case 3:
                _i++;
                return [3 /*break*/, 1];
            case 4: return [2 /*return*/];
        }
    });
}
export function valueComparator(name) {
    var computeValue = isString(name) ? function (obj) { return obj && obj[name]; } : name;
    return function (left, right) {
        var computedLeft = computeValue(left);
        var computedRight = computeValue(right);
        if (isNil(computedLeft)) {
            if (isNil(computedRight)) {
                return 0;
            }
            else {
                return 1;
            }
        }
        else if (isNil(computedRight)) {
            return -1;
        }
        if (computedLeft < computedRight) {
            return -1;
        }
        else if (computedLeft > computedRight) {
            return 1;
        }
        else {
            return 0;
        }
    };
}
export function negateComparator(comparator) {
    return function (left, right) { return -comparator(left, right); };
}
export function composeComparators() {
    var comparators = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        comparators[_i] = arguments[_i];
    }
    return function (left, right) { return comparators.reduce(function (rank, comparator) { return rank === 0 ? comparator(left, right) : rank; }, 0); };
}
export function sortedIndexOfBy(array, value, it) {
    var length = isNil(array) ? 0 : array.length;
    if (length) {
        var iteratee = getIteratee(it);
        var index = sortedIndexBy(array, value, iteratee);
        if (index < length && iteratee(array[index]) === iteratee(value)) {
            return index;
        }
    }
    return -1;
}
export function sortedIndexWith(array, value, comparator) {
    var low = 0;
    var high = array.length;
    while (low < high) {
        var mid = (low + high) >>> 1; // tslint:disable-line:no-bitwise
        var computed = array[mid];
        if (comparator(computed, value) < 0) {
            low = mid + 1;
        }
        else {
            high = mid;
        }
    }
    return high;
}
export function orderByComparator(array, comparator) {
    var copied = array.concat();
    copied.sort(comparator);
    return copied;
}
export function firstByComparator(items, comparator) {
    return first(orderByComparator(items, comparator));
}
export function withoutIndex(array, index) {
    return array.slice(0, index).concat(array.slice(index + 1));
}
export function withValueByIndex(array, index, value) {
    return array.slice(0, index).concat([value]).concat(array.slice(index));
}
/**
 * This function is an analogous of {@link Array#sort} method,
 * but it guarantees predictable order of items having the same sorting order.
 *
 * Items having the same sorting order will stay in the same order as in the original array.
 */
export function mutableSort(arr, comparator) {
    var arrWithIndex = arr.map(function (value, index) { return ({ value: value, index: index }); });
    arrWithIndex.sort(function (left, right) {
        var byComparator = comparator(left.value, right.value);
        return byComparator === 0 ? left.index - right.index : byComparator;
    });
    arrWithIndex.forEach(function (_a, i) {
        var value = _a.value;
        return arr[i] = value;
    });
}
/**
 * Finds maximal array element using given comparator
 */
export function maxWith(arr, comparator) {
    return arr.reduce(function (max, el) { return isNotNil(max)
        ? (comparator(max, el) < 0 ? el : max)
        : el; }, void 0);
}
